import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import MarketingVideoCards from "@/components/MarketingVideoCards";
import {
  ArrowRight, PlugsConnected, Receipt, Sparkle, ArrowsClockwise, ChartLineUp,
  CalendarCheck, Stack, ShieldCheck, FileArrowDown, ChatCircleText, ClockCounterClockwise,
  PencilSimpleLine, CheckCircle, Buildings,
} from "@phosphor-icons/react";

const ASSISTANT = "/lottie-features.jpg";

const LOTTIE_WORDS = [
  ["L", "edger"], ["O", "ptimisation"], ["T", "hrough"],
  ["T", "ask"], ["I", "ntelligence"], ["E", "fficiency"],
];

const GROUPS = [
  {
    tag: "Connect & import",
    title: "Straight from your ledger",
    items: [
      { icon: PlugsConnected, t: "Live Xero integration", d: "OAuth connection to one or many organisations. Invoices, bills, accounts and the trial balance read directly from Xero." },
      { icon: Receipt, t: "Automatic register build", d: "Line items posted to your chosen nominal codes become schedules — accruals, prepayments and deferred income — with no manual entry." },
      { icon: PencilSimpleLine, t: "Manual adjustments", d: "Create one-off or opening-balance schedules by hand when something lives outside Xero, with the same release logic." },
    ],
  },
  {
    tag: "Automate",
    title: "Month-end that runs itself",
    items: [
      { icon: Sparkle, t: "Deferred income & revenue recognition", d: "A dedicated revenue-recognition module releases income across the period earned, straight-line or on a custom basis." },
      { icon: ArrowsClockwise, t: "Recurring accrual engine", d: "Repeating bills accrue and reverse automatically each period so nothing is missed at close." },
      { icon: CalendarCheck, t: "Release journals", d: "Monthly journals post on schedule — automatically or behind a two-person approval — straight back to Xero." },
    ],
  },
  {
    tag: "Report & forecast",
    title: "See the whole picture",
    items: [
      { icon: ChartLineUp, t: "12-month forecast", d: "Opening balance, monthly releases and closing position twelve months ahead, by category and entity." },
      { icon: ShieldCheck, t: "Reconciliation & accuracy", d: "Compare the register to the live Xero balance per nominal code, surface variances, and true them up or accept with a reason." },
      { icon: FileArrowDown, t: "Branded PDF & Excel exports", d: "Practice-branded register, forecast, reconciliation and balance reports — board-ready in a click." },
    ],
  },
  {
    tag: "Control & collaborate",
    title: "Confidence at sign-off",
    items: [
      { icon: CalendarCheck, t: "Month-end packs", d: "Lock the period and produce a signed-off month-end pack with everything reviewers need in one place." },
      { icon: ChatCircleText, t: "Review notes & @mentions", d: "Preparer-to-reviewer threads on any schedule or pending journal, with open queries surfaced on the dashboard." },
      { icon: ClockCounterClockwise, t: "Full audit history", d: "Every create, amend, post and approval is logged with before/after detail and exportable activity reports." },
    ],
  },
  {
    tag: "Scale",
    title: "Built for the whole portfolio",
    items: [
      { icon: Stack, t: "Multi-entity portfolios", d: "Work across every client organisation from one portal, each with its own isolated chart of accounts and codes." },
      { icon: Buildings, t: "Per-entity configuration", d: "Classify nominal codes independently per organisation — no cross-contamination between clients." },
      { icon: ShieldCheck, t: "Roles & read-only reviewers", d: "Admins manage the team; viewers can leave notes but never touch financial data or exports." },
    ],
  },
];

export default function Features() {
  return (
    <MarketingLayout>
      <Seo title="Features" path="/features"
        description="See how Lottie automates accruals, prepayments and deferred income in Xero — smart schedules, recurring accruals, a 12-month forecast, reconciliation and branded PDF/Excel reports." />
      <section className="overflow-hidden">
       <div className="max-w-6xl mx-auto px-6 pt-16 pb-12 grid lg:grid-cols-2 gap-10 items-center">
        <div className="fade-up relative z-10">
          <div className="overline mb-4" style={{ color: "#F37A24" }}>Features</div>
          <h1 className="font-heading font-black text-navy text-4xl sm:text-5xl leading-[1.06] tracking-tighter">
            One assistant for the entire month-end cycle
          </h1>
          <p className="text-navy/70 text-base sm:text-lg mt-5 max-w-lg leading-relaxed">
            From the moment you connect Xero to the signed-off month-end pack, Lottie handles the schedules, journals,
            reconciliation and reporting so your team can focus on the review, not the rekeying.
          </p>
        </div>
        <div className="fade-up relative z-0">
          <div className="relative aspect-[4/5]">
            <img src={ASSISTANT} alt="Lottie the dog with a Xero ball" data-testid="features-hero-image"
              className="absolute inset-y-0 right-0 h-full w-full lg:w-[128%] max-w-none object-cover"
              style={{
                objectPosition: "center 38%",
                WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 32%)",
                maskImage: "linear-gradient(to right, transparent 0%, #000 32%)",
              }} />
            {/* LOTTIE acronym — overlaid over the dog */}
            <div className="absolute z-10 bottom-6 inset-x-0 px-4 flex justify-center pointer-events-none" data-testid="features-lottie-acronym">
              <p className="font-heading tracking-tight text-sm sm:text-base text-center rounded-full px-5 py-2.5 shadow-lg"
                style={{ background: "rgba(14,15,63,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.12)" }}>
                {LOTTIE_WORDS.map(([cap, rest], i) => (
                  <span key={i}>
                    <span className="font-black" style={{ color: "#F37A24" }}>{cap}</span>
                    <span className="font-bold text-white">{rest}</span>
                    {i < LOTTIE_WORDS.length - 1 && (
                      <span className="text-white">{i === LOTTIE_WORDS.length - 2 ? " \u0026 " : "\u00a0\u00a0"}</span>
                    )}
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>
       </div>
      </section>

      {GROUPS.map((g, gi) => (
        <section key={g.tag} className={gi % 2 === 1 ? "bg-white border-y border-slate-200" : ""} data-testid={`feature-group-${g.tag}`}>
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="overline mb-2" style={{ color: "#F37A24" }}>{g.tag}</div>
            <h2 className="font-heading font-bold text-navy text-2xl sm:text-3xl tracking-tight mb-10">{g.title}</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {g.items.map((it) => (
                <div key={it.t} className="flex flex-col gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(242,111,33,0.1)" }}>
                    <it.icon size={20} weight="duotone" style={{ color: "#F37A24" }} />
                  </div>
                  <h3 className="font-heading font-bold text-navy">{it.t}</h3>
                  <p className="text-navy/70 text-sm leading-relaxed">{it.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <div className="bg-white border-y border-slate-200">
        <MarketingVideoCards />
      </div>

      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <h2 className="font-heading font-bold text-navy text-3xl sm:text-4xl tracking-tight">Ready to put month-end on autopilot?</h2>
        <div className="flex items-center justify-center gap-2 mt-4 text-navy/70 text-sm">
          <CheckCircle size={17} weight="fill" className="text-positive" /> 7-day free trial · no card required
        </div>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <Link to="/start" data-testid="features-cta-trial" className="text-white font-semibold px-6 py-3.5 rounded-xl flex items-center gap-2" style={{ background: "#F37A24" }}>
            Start free trial <ArrowRight size={18} weight="bold" />
          </Link>
          <Link to="/pricing" data-testid="features-cta-pricing" className="bg-white text-navy font-semibold px-6 py-3.5 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
            View pricing
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
}
